import React, { useEffect, useRef, useState } from 'react';
import ePub from 'epubjs';
import { useLocation, useNavigate } from 'react-router-dom';
import PDFLoader from './PDFLoader';

const EPUBViewer = () => {
  const location = useLocation();
  const url = location.state?.message;
  const [rendition, setRendition] = useState(null);
  const [isLoading, setIsLoading] = useState(true); // New state for loading
  const viewerRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  const navigate = useNavigate();

  const goBack = () => {
    if (window.history.length > 1) {
      navigate(-1); // Equivalent to window.history.back()
    } else {
      alert('No history available to go back.');
    }
  };

  useEffect(() => {
    if (url) {
      const book = ePub(url);
      book.ready.then(() => {
        const renditionInstance = book.renderTo(viewerRef.current, {
          width: '100%',
          height: '100%',
          manager: 'continuous',
        });

        setRendition(renditionInstance);
        renditionInstance.display().then(() => {
          setIsLoading(false); // Hide loader when the book is ready
        }).catch(error => {
          console.error('Error displaying book:', error);
          setIsLoading(false); // Hide loader even if there's an error
        });
      }).catch(error => {
        console.error('Error loading book:', error);
        setIsLoading(false); // Hide loader if book fails to load
      });

      return () => {
        if (rendition) {
          rendition.destroy();
        }
        book.destroy();
      };
    }
  }, [url]);

  const goToNextPage = () => {
    if (rendition) {
      rendition.next().catch(err => {
        console.error('Failed to go to next page:', err);
      });
    }
  };

  const goToPreviousPage = () => {
    if (rendition) {
      rendition.prev().catch(err => {
        console.error('Failed to go to previous page:', err);
      });
    }
  };

  const divRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      divRef.current.requestFullscreen();
    }
  };

  const onChange = () => {
    setIsFullscreen(!!document.fullscreenElement);
  };

  useEffect(() => {
    document.addEventListener('fullscreenchange', onChange);

    // Cleanup listener on component unmount
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
    };
  }, []);


  const zoomIn = () => {
    setZoom(prevZoom => {
      const newZoom = prevZoom + 0.1;
      if (rendition) {
        rendition.themes.fontSize(`${newZoom * 100}%`);
      }
      return newZoom;
    });
  };

  const zoomOut = () => {
    setZoom(prevZoom => {
      const newZoom = prevZoom - 0.1;
      if (rendition) {
        rendition.themes.fontSize(`${newZoom * 100}%`);
      }
      return newZoom;
    });
  };

  const resetZoom = () => {
    setZoom(1); // Reset zoom state to default (1)
    if (rendition) {
      rendition.themes.fontSize('100%'); // Apply default font size
    }
  };

  const formatDate = (date) => {
    const offset = 5.5 * 60 * 60 * 1000;
    const istDate = new Date(date.getTime() + offset);
    return istDate.toISOString().replace('T', ' ').split('.')[0];
  };
  const baseURL = process.env.REACT_APP_BASE_URL;
  const apikey = process.env.REACT_APP_API_KEY;
  const bookid = location?.state?.id;
  const user_id = localStorage.getItem('userStatID');

  useEffect(() => {

    const startTime = new Date();

    return () => {
      const endTime = new Date();
      const reading_seconds = Math.floor((endTime - startTime) / 1000);

      const payload = [
        {
          bookid,
          user_id,
          starttime: formatDate(startTime),
          endtime: formatDate(endTime),
          reading_seconds
        }
      ];

      fetch(`${baseURL}/insert_reading_sessions/`, {
        method: 'POST',
        headers: {
          'Authorization': apikey,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      })
        .then(async response => {
          if (!response.ok) {
            const text = await response.text();
            throw new Error(`HTTP error! Status: ${response.status}, Response: ${text}`);
          }
          return response.json();
        })
        .catch(error => {
          console.error('Error sending reading session:', error);
        });
    };
  }, [apikey, baseURL, bookid, user_id]);



  if (!url) {
    return <div>Loading...</div>;
  }

  return (
    <>


      <section ref={divRef} className={`pdf-viewer ${isFullscreen ? 'fullscreen' : ''}`}>
        <div className='container'>
          <div className='row'>
            <div className='d-flex justify-content-between align-items-center d-none'>
              <button className="btn-6 view-all-btnn mb-4 bck_btn" onClick={goBack} tabIndex={-1}><i className="fas fa-long-arrow-alt-left me-2"></i>Back </button>

              <button className="btn-6 view-all-btnn mb-4 bck_btn" onClick={toggleFullscreen}>
                {isFullscreen ? 'Exit Fullscreen' : 'Go Fullscreen'}
              </button>
            </div>
            <div className='d-flex justify-content-between align-items-center'>
              <button className="btn-6 view-all-btnn mb-4" onClick={goBack}><i className="fas fa-long-arrow-alt-left me-2"></i>Back</button>

              <div className="mb-4">
                <button className="btn f_increase view-all-btnn me-2" onClick={zoomIn}><i className="fas fa-search-plus"></i></button>
                <button className="btn f_increase view-all-btnn me-2" onClick={resetZoom} ><i className="fas fa-redo-alt"></i></button>
                <button className="btn f_increase view-all-btnn me-2" onClick={zoomOut}><i className="fas fa-search-minus"></i></button>
                <button className="btn f_increase view-all-btnn " onClick={toggleFullscreen}>
                  <i className={isFullscreen ? "fa fa-compress" : "fa fa-expand"}></i>
                </button>
              </div>
            </div>

            <div className='col-md-12 mt_0 text-upper epub_books'>
              <div ref={viewerRef} style={{ position: 'relative', height: '100%', background: '#fff', borderRadius: '33px' }}>
                {isLoading && (
                  <PDFLoader />
                )}

                {!isLoading && (
                  <div className="navigation-controlss">
                    <button className="carousel-control-prev" type="button" onClick={goToPreviousPage}>
                      <span className="btn-6 view-all-btnn view-all-btnn-cr" aria-hidden="true">
                        <img src="data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23fff'%3e%3cpath d='M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z'/%3e%3c/svg%3e" alt="Arrow Icon" />
                      </span>
                      <span className="visually-hidden">Previous</span>
                    </button>
                    <button className="carousel-control-next" type="button" onClick={goToNextPage}>
                      <span className="btn-6 view-all-btnn view-all-btnn-cr" aria-hidden="true">
                        <img src="data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16' fill='%23fff'%3e%3cpath d='M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z'/%3e%3c/svg%3e" alt="Arrow Icon" />
                      </span>
                      <span className="visually-hidden">Next</span>
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>


      </section>
    </>
  );
};

export default EPUBViewer;
